/* eslint-disable react-refresh/only-export-components */
import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  createContext
} from "react";
import api from "api/landlord/auth";
import {
  buildPmsFlags,
  isAnyPmsActive,
  isPmsActive,
  type PmsFlags
} from "utils/pms";
import { jwt } from "api/jwt";
import {
  DashboardPayload,
  getCurrentPartner,
  getDashboard,
  getPartnerDefaultEnrollments,
  PartnerDefaultEnrollmentsPayload
} from "api/landlord/landlord";
import { FEATURES, Partner as PartnerRes } from "types/landlord/partner";
import {
  PartnerFeatures,
  PropertyManagementSystem
} from "shared/types/partner";
import { useIntercom } from "react-use-intercom";
import { usePostHog } from "posthog-js/react";
import config from "config/config";
import { SupportedPropertyManagementSystem } from "utils/supportedPMS";

export enum SERVICE_TYPE {
  LANDLORD = "landlord",
  FULL_SERVICE = "full_service"
}

export enum ROLE {
  Admin = "admin",
  PropertyManager = "property_manager",
  Owner = "owner"
}

export enum BillingType {
  SingleAccount = "single_account",
  PerProperty = "per_property"
}

export enum BillingPeriod {
  Monthly = "monthly",
  Weekly = "weekly",
  Daily = "daily" // not used at the moment of 2025-08-08
}

/**
 * @description PMSes allowed to create enrollments, units and properties on BoomReport,
 * overriding the default behavior which restricts enrollment
 * though Boom platform, and only allows PMSes to enroll customers via
 * their own platform.
 */
const DirectBoomCreationPMSes = [PropertyManagementSystem.Appfolio];

export type ColumnVisibilityItem = {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  always_on?: boolean;
};

type Address = {
  address1: string;
  address2: string;
  city: string;
  country: string;
  lat: unknown;
  lng: unknown;
  postal_code: string;
  state_or_province: string;
};

type Property = {
  address: Address;
  can_delete: boolean;
  created_at: string;
  id: number;
  name: string;
  number_of_tenants: number;
  pms_id?: string;
};

type Authed = {
  id: number;
  obfuscated_id: string;
  email: string;
  name: string;
  partner: Partner;
  role: ROLE;
  created_at?: string;
  can_change_available_by_date: boolean;
  can_change_rent_amount: boolean;
  can_make_decisions: boolean;
  can_view_reports: boolean;
  can_cancel_applications: boolean;
  can_change_unit: boolean;
  can_edit_income: boolean;
  can_manage_all_properties: boolean;
  can_override_decision_recommendation: boolean;
  can_show_full_ssn: boolean;
  can_change_applicant_role: boolean;
  can_subscribe_renters: boolean;
  can_edit_subscription_details: boolean;
  can_unsubscribe_renters: boolean;
  can_view_renters_list: boolean;
  can_view_rent_reporting: boolean;
  can_view_credit_reporting: boolean;
  can_apply_active_discounts: boolean;
  can_apply_custom_discounts: boolean;
  can_run_reports_manually: boolean;
  can_edit_application_rent: boolean;
  can_override_applicant_screening_template: boolean;
  can_change_desired_move_in_date: boolean;
  can_access_screening_templates?: boolean;
  can_access_discount_codes?: boolean;
  boom_report_managers_allowed: boolean;
  can_access_reports?: {
    rental_application: boolean;
    identity_verification: boolean;
    income_report: boolean;
    housing_history_report: boolean;
    credit_report: boolean;
    us_criminal_report: boolean;
    ca_criminal_report: boolean;
    ca_credit_report: boolean;
    housing_court_report: boolean;
    animal_screening: boolean;
    disclosure: boolean;
    additional_documents: boolean;
    warnings_report: boolean;
  };
  can_edit_idv?: {
    override_failed_idv: boolean;
    retry_failed_idv: boolean;
    rerun_idv_report?: boolean;
    edit_idv_details: boolean;
  };
  can_resend_invitation?: boolean;
  can_copy_application_link?: boolean;
  can_access_billing_settings?: boolean;
  can_access_users_settings?: boolean;
  can_make_voice_calls?: boolean;
  // Not a permission but a plain column (CRM-1169), so — unlike its `can_*`
  // neighbours — it is never implied by the Admin/Owner role.
  receives_inbound_calls?: boolean;
  // CRM-1356: "every number I can access" mode + the hand-picked ids under it.
  inbound_all_numbers?: boolean;
  inbound_number_ids?: number[];
  can_refund_application?: boolean;
  can_access_explain_recommendation_modal?: boolean;
  can_edit_estimated_monthly_payments?: boolean;
  properties: Property[];
  notification_settings: {
    application_started: boolean;
    application_submitted: boolean;
    application_group_submitted?: boolean;
    applicant_submitted_application?: boolean;
    rental_history_verification_completed: boolean;
  };
  crm_notification_settings?: {
    task_assigned: boolean;
    task_due_soon: boolean;
    mentioned_in_tasks: boolean;
    lead_assigned_to_me?: boolean;
    mentioned_in_lead_notes?: boolean;
    lead_stage_changed?: boolean;
    lead_created?: boolean;
    inbox_message_in_app?: boolean;
    inbox_message_channels?: {
      email: boolean;
      sms: boolean;
      chat: boolean;
    };
    inbox_message_delivery_timing?: "immediate" | "digest";
    inbox_message_digest_frequency?:
      | "30_minutes"
      | "1_hour"
      | "2_hours"
      | "3_hours"
      | "24_hours";
  };
  boom_report_notification_settings?: {
    renter_unenrolled?: boolean;
  };
  stream_chat_token?: string;
  stream_chat_id?: string;
  columns_visibility?: Record<string, ColumnVisibilityItem[]>;
  ui_settings?: { nav?: { order: string[]; hidden: string[] } };
  intercom_id?: string;
};

type Partner = {
  id?: number;
  approved?: boolean;
  key_completed?: boolean;
  status: "active" | "started" | "rejected"; // verify if rejected status is a thing
  billing_completed?: boolean;
  billing_type: BillingType;
  billing_period: BillingPeriod;
  pending_billing_period?: BillingPeriod;
  pending_billing_period_effective_at?: string;
  can_update_billing_type: boolean;
  access_key: string;
  email: string;
  name: string;
  properties: Property[];
  service_type: SERVICE_TYPE;
  property_management_system?: string;
  url: string;
  opt_out_period_days: number;
  opt_in_period_days: number;
  unexpired_opt_in_period: boolean;
  notification_settings: {
    application_started: boolean;
    application_submitted: boolean;
    application_group_submitted?: boolean;
    applicant_submitted_application?: boolean;
    rental_history_verification_completed: boolean;
  };
};

type Auth = undefined | null | Authed;

export type PartnerUserPermissions = {
  canChangeAvailablebyDate: boolean;
  canChangeRentAmount: boolean;
  canMakeDecisions: boolean;
  canViewReports: boolean;
  canManageProperties: boolean;
  canCancelApplications: boolean;
  canChangeUnit: boolean;
  canEditIncome: boolean;
  canOverrideDecisionRecommendation: boolean;
  canManageAllProperties: boolean;
  canShowFullSsn: boolean;
  canChangeApplicantRole: boolean;
  canUpdateBillingType: boolean;
  canSubscribeRenters: boolean;
  canEditSubscriptionDetails: boolean;
  canUnsubscribeRenters: boolean;
  canViewRentersList: boolean;
  canViewRentReporting: boolean;
  canViewCreditReporting: boolean;
  boomReportWriteAccess: boolean;
  canApplyActiveDiscounts: boolean;
  canApplyCustomDiscounts: boolean;
  canRunReportsManually: boolean;
  canEditApplicationRent: boolean;
  canOverrideApplicantScreeningTemplate?: boolean;
  canAccessScreeningTemplates: boolean;
  canManageDiscounts: boolean;
  canAccessReports: {
    rentalApplication: boolean;
    identityVerification: boolean;
    incomeReport: boolean;
    housingHistoryReport: boolean;
    creditReport: boolean;
    usCriminalReport: boolean;
    caCriminalReport: boolean;
    caCreditReport: boolean;
    housingCourtReport: boolean;
    animalScreening: boolean;
    disclosure: boolean;
    additionalDocumentsReport: boolean;
    warningsReport: boolean;
  };
  canEditIdv: {
    overrideFailedIdv: boolean;
    retryFailedIdv: boolean;
    rerunIdvReport: boolean;
    editIdvDetails: boolean;
  };
  canResendInvitation: boolean;
  canCopyApplicationLink: boolean;
  canAccessBillingSettings: boolean;
  canAccessUsersSettings: boolean;
  canChangeDesiredMoveInDate: boolean;
  canMakeVoiceCalls: boolean;
  receivesInboundCalls: boolean;
  canRefundApplication: boolean;
  canAccessExplainRecommendationModal: boolean;
  canEditEstimatedMonthlyPayments: boolean;
};

type Context = {
  userTimeZone?: string;

  auth: Auth;
  partner?: PartnerRes;
  dashboard?: DashboardPayload;
  partnerDefaultEnrollments?: PartnerDefaultEnrollmentsPayload;
  refreshPartnerData: () => Promise<
    [
      PromiseSettledResult<void>,
      PromiseSettledResult<void>,
      PromiseSettledResult<void>
    ]
  >;
  refreshPartnerDefaultEnrollments: () => Promise<void>;
  refreshAuthUserDashboardState: () => void;
  refreshAuth: () => Promise<void>;
  partnerFeatures: PartnerFeatures[];
  type: {
    isManual: boolean;
    isSDK: boolean;
    isPMS: boolean;
    isBoomScreen: boolean;
    isBoomCRM: boolean;
    isProvisionedPhoneNumber: boolean;
    isCommunication: boolean;
    isCommunicationSms: boolean;
    isNativeChat: boolean;
    // native_chat is the migration flag; chat is still switchable per partner in
    // settings. Both must be on before any chat surface (Inbox entry, composer
    // tab) is worth showing — otherwise the partner lands on an empty Inbox.
    isNativeChatActive: boolean;
    isCommunicationPhone: boolean;
    // AI leasing agent (crm_ai_voice_agent) — also gates AI suggested replies
    // and auto-send, superseding the retired per-surface flags.
    isCrmAiVoiceAgent: boolean;
    isCrmFeatureWorkflows: boolean;
    isTenantLeads: boolean;
    isTasks: boolean;
    isLockboxes: boolean;
    isCollectionsOutstandingBalance: boolean;
    isMasterSearch: boolean;
    isSyndication: boolean;
    isShowings: boolean;
    isMarketRent: boolean;
    isCrmListingSync: boolean;
    isPmsListingSync: boolean;
    /** Listing settings (listing templates) ride the showings entitlement (CRM-908). */
    isListingSettings: boolean;
    isCommunicationEmail: boolean;
    isCrmAnalytics: boolean;
    isCustomizeLeadSourcesStages: boolean;
    isOwnerLeads: boolean;
    isRentReporting: boolean;
    isBillPerProperty: boolean;
    isPropertyGroupMarket: boolean;
    isOnboardingChecklist: boolean;
    isAddressValidationEnabled: boolean;

    isFullService: boolean;
    isLandlord: boolean;
    isBoomOps: boolean;
    adminPrivileges: boolean;
    isPropertyManager: boolean;
    isSitesAndCommunities: boolean;
    /**
     * @description Whether the partner can enroll customers, create units and properties through Boom, and not forced to use their own PMS platform for enrollments.
     */
    canManageEntitiesDirectly: boolean; // can enroll customers directly through Boom platform (if `isPMS` and `isRentReporting` are true),
    isShowFullSsn: boolean;
    isEnterprisePropertyware: boolean;
    isPmsListingSyncEnabled: boolean;
    isHomeSiteRentEnabled: boolean;
    isWeeklyBillingEnabled: boolean;
    hasTemplateFeeCollection: boolean;
    isMultiPMS: boolean;
    isStripeManagedBilling: boolean;
    isBoomScreenOutcomes: boolean;
    /**
     * @description When enabled, the rent reporting list query returns a precomputed `account_status`
     * per renter, so the client reads it directly instead of deriving it from the granular
     * verification/case status fields.
     */
    isCombinedSearchV2: boolean;
  };
  permissions: PartnerUserPermissions;
  pms: PmsFlags;
};

const AppContext = createContext<Context>({
  auth: undefined,
  partner: undefined,
  dashboard: undefined,
  partnerDefaultEnrollments: undefined,
  refreshAuth: () => Promise.resolve(),
  refreshPartnerData: () =>
    Promise.allSettled([
      Promise.resolve(),
      Promise.resolve(),
      Promise.resolve()
    ]),
  refreshPartnerDefaultEnrollments: () => Promise.resolve(),
  refreshAuthUserDashboardState: () => {},
  partnerFeatures: [],
  type: {
    isManual: false,
    isSDK: false,
    isPMS: false,
    isBoomScreen: false,
    isBoomCRM: false,
    isProvisionedPhoneNumber: false,
    isCommunication: false,
    isCommunicationSms: false,
    isNativeChat: false,
    isNativeChatActive: false,
    isCommunicationPhone: false,
    isCrmAiVoiceAgent: false,
    isCrmFeatureWorkflows: false,
    isTenantLeads: false,
    isTasks: false,
    isLockboxes: false,
    isCollectionsOutstandingBalance: false,
    isMasterSearch: false,
    isSyndication: false,
    isShowings: false,
    isMarketRent: false,
    isCrmListingSync: false,
    isPmsListingSync: false,
    isListingSettings: false,
    isCommunicationEmail: false,
    isCrmAnalytics: false,
    isCustomizeLeadSourcesStages: false,
    isOwnerLeads: false,
    isRentReporting: false,
    isBillPerProperty: false,
    isPropertyGroupMarket: false,
    isOnboardingChecklist: false,
    isAddressValidationEnabled: false,

    isFullService: false,
    isLandlord: false,
    isBoomOps: false,
    isPropertyManager: false,
    isSitesAndCommunities: false,
    canManageEntitiesDirectly: false,
    isShowFullSsn: false,
    adminPrivileges: false,
    isEnterprisePropertyware: false,
    isPmsListingSyncEnabled: false,
    isHomeSiteRentEnabled: false,
    isWeeklyBillingEnabled: false,
    hasTemplateFeeCollection: false,
    isMultiPMS: false,
    isStripeManagedBilling: false,
    isBoomScreenOutcomes: false,
    isCombinedSearchV2: false
  },
  permissions: {
    canChangeAvailablebyDate: false,
    canChangeRentAmount: false,
    canMakeDecisions: false,
    canViewReports: false,
    canManageProperties: false,
    canChangeUnit: false,
    canEditIncome: true,
    canOverrideDecisionRecommendation: true,
    canShowFullSsn: false,
    canChangeApplicantRole: false,
    canUpdateBillingType: false,
    canSubscribeRenters: false,
    canEditSubscriptionDetails: false,
    canUnsubscribeRenters: false,
    canViewRentersList: false,
    canViewRentReporting: false,
    canViewCreditReporting: false,
    canCancelApplications: false,
    canManageAllProperties: false,
    boomReportWriteAccess: false,
    canApplyActiveDiscounts: false,
    canApplyCustomDiscounts: false,
    canRunReportsManually: false,
    canOverrideApplicantScreeningTemplate: false,
    canEditApplicationRent: false,
    canAccessScreeningTemplates: false,
    canManageDiscounts: false,
    canAccessReports: {
      rentalApplication: false,
      identityVerification: false,
      incomeReport: false,
      housingHistoryReport: false,
      creditReport: false,
      usCriminalReport: false,
      caCriminalReport: false,
      caCreditReport: false,
      housingCourtReport: false,
      animalScreening: false,
      disclosure: false,
      additionalDocumentsReport: false,
      warningsReport: false
    },
    canEditIdv: {
      overrideFailedIdv: false,
      retryFailedIdv: false,
      rerunIdvReport: false,
      editIdvDetails: false
    },
    canResendInvitation: false,
    canCopyApplicationLink: false,
    canAccessBillingSettings: false,
    canAccessUsersSettings: false,
    canChangeDesiredMoveInDate: false,
    canMakeVoiceCalls: false,
    receivesInboundCalls: false,
    canRefundApplication: false,
    canAccessExplainRecommendationModal: false,
    canEditEstimatedMonthlyPayments: false
  },
  pms: buildPmsFlags({})
});

export const AuthProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  /**
   * Defines if there is an authed user
   * Null - if we could not determine auth (failed request etc.)
   * undefined - if we did not check for auth yet
   * Object - if auth is available!
   */
  const [auth, setAuth] = useState<Auth>(undefined);
  const [partner, setPartner] = useState<PartnerRes>();
  const [dashboard, setDashboard] = useState<DashboardPayload>();
  const [partnerDefaultEnrollments, setPartnerDefaultEnrollments] =
    useState<PartnerDefaultEnrollmentsPayload>();

  const posthog = usePostHog();
  const intercom = useIntercom();

  const clearSession = useCallback(() => {
    posthog.reset();
    intercom.shutdown();
    setAuth(null);
    intercom.boot({ customAttributes: { source: "partner_portal" } });
  }, [posthog, intercom]);

  const refreshAuth = () => {
    return api
      .checkAuth()
      .then((user: Authed) => {
        return refreshPartnerData().then(() => user);
      })
      .then((user) => {
        setAuth(user);
        posthog.reset();
        intercom.shutdown();
        if (user) {
          posthog.identify(user.id.toString(), {
            name: user.name,
            email: user.email,
            partner_id: user.partner?.id || "no_partner_id",
            property_management_system:
              user.partner?.property_management_system || "no_pms",
            partner_name: user.partner?.name || "no_partner_name",
            env: config.environment || "dev"
          });
          intercom.boot({
            name: user.name,
            email: user.email,
            userId: user.intercom_id,
            customAttributes: {
              source: "partner_portal"
            },
            hideDefaultLauncher: true
          });
        } else
          intercom.boot({
            customAttributes: {
              source: "partner_portal"
            }
          });
      })
      .catch(() => {
        posthog.reset();
        intercom.shutdown();
        jwt.clearTokens();
        clearSession();
      });
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  useEffect(() => {
    window.addEventListener("auth:session-expired", clearSession);
    return () =>
      window.removeEventListener("auth:session-expired", clearSession);
  }, [clearSession]);

  const refreshPartnerData = () => {
    return Promise.allSettled([
      getCurrentPartner()
        .then((partner) => setPartner(partner.partner))
        .catch(() => {
          // silent fail
        }),
      getDashboard()
        .then((dashboard) => setDashboard(dashboard))
        .catch(() => {
          // silent fail
        }),
      getPartnerDefaultEnrollments()
        .then((enrollments) => setPartnerDefaultEnrollments(enrollments))
        .catch(() => {
          // silence error
        })
    ]);
  };

  const refreshPartnerDefaultEnrollments = () => {
    return getPartnerDefaultEnrollments()
      .then((enrollments) => setPartnerDefaultEnrollments(enrollments))
      .catch(() => {
        // silence error
      });
  };

  const refreshAuthUserDashboardState = () => {
    api.checkAuth().then((user: Authed) => {
      setAuth(user);
    });
  };

  const isMultiPMS = !!partner?.features.includes(
      FEATURES.MULTIPLE_PROPERTY_MANAGEMENT_SYSTEMS
    ),
    isSDK = !!partner?.features.includes(FEATURES.SDK),
    isPMS =
      (!!partner?.property_management_system?.name &&
        SupportedPropertyManagementSystem.includes(
          partner.property_management_system.name
        )) ||
      isMultiPMS,
    isBoomScreen = !!partner?.features.includes(FEATURES.BOOM_SCREEN),
    isBoomCRM = !!partner?.features.includes(FEATURES.BOOM_CRM),
    isProvisionedPhoneNumber = !!partner?.features.includes(
      FEATURES.PROVISIONED_PHONE_NUMBER
    ),
    isCommunication = !!partner?.features.includes(FEATURES.COMMUNICATION),
    isCommunicationSms = !!partner?.features.includes(
      FEATURES.COMMUNICATION_SMS
    ),
    isNativeChat = !!partner?.features.includes(FEATURES.NATIVE_CHAT),
    isNativeChatActive =
      isNativeChat &&
      !!partner?.partner_features?.includes(PartnerFeatures.Chat),
    isCommunicationPhone = !!partner?.features.includes(
      FEATURES.COMMUNICATION_PHONE
    ),
    isCrmAiVoiceAgent = !!partner?.features.includes(
      FEATURES.CRM_AI_VOICE_AGENT
    ),
    // Plan-managed "workflows" entitlement; crm_feature_workflows is the
    // legacy flag the API still emits as an alias (safe across deploy order).
    isCrmFeatureWorkflows = !!(
      partner?.features.includes(FEATURES.WORKFLOWS) ||
      partner?.features.includes(FEATURES.CRM_FEATURE_WORKFLOWS)
    ),
    isTenantLeads = !!partner?.features.includes(FEATURES.TENANT_LEADS),
    isTasks = !!partner?.features.includes(FEATURES.TASKS),
    isLockboxes = !!partner?.features.includes(FEATURES.LOCKBOXES),
    // The flag alone is not enough: the list is Rent Manager only (BF-454), and
    // the endpoint behind it needs a PMS connection, so a partner without one
    // would get a nav entry that can only error.
    isCollectionsOutstandingBalance =
      !!partner?.features.includes(
        FEATURES.UI_COLLECTIONS_OUTSTANDING_BALANCE
      ) &&
      isPmsActive({
        partner,
        pms: PropertyManagementSystem.RentManager,
        isMultiPMS
      }),
    isMasterSearch = !!partner?.features.includes(FEATURES.UI_MASTER_SEARCH),
    isSyndication = !!partner?.features.includes(FEATURES.SYNDICATION),
    isShowings = !!partner?.features.includes(FEATURES.SHOWINGS),
    isMarketRent = !!partner?.features.includes(FEATURES.MARKET_RENT),
    isCrmListingSync = !!partner?.features.includes(FEATURES.CRM_LISTING_SYNC),
    isPmsListingSync = !!partner?.features.includes(FEATURES.PMS_LISTING_SYNC),
    // Listing settings (listing templates) ride the showings entitlement: the
    // CRM-908 spec puts "the Showings calendar and Listing settings" under the
    // one flag, and showings is in the API's legacy boom_crm bundle, so no plan
    // test is needed. Mirrors the API's ensure_listing_settings_access!.
    isListingSettings = isBoomCRM && isShowings,
    isCommunicationEmail = !!partner?.features.includes(
      FEATURES.COMMUNICATION_EMAIL
    ),
    isCrmAnalytics = !!partner?.features.includes(FEATURES.CRM_ANALYTICS),
    isCustomizeLeadSourcesStages = !!partner?.features.includes(
      FEATURES.CUSTOMIZE_LEAD_SOURCES_STAGES
    ),
    isOwnerLeads = !!partner?.features.includes(FEATURES.OWNER_LEADS),
    isRentReporting = !!partner?.features.includes(FEATURES.RENT_REPORTING),
    isManual = !isSDK && !isPMS && isRentReporting,
    isBillPerProperty = partner?.billing_type === BillingType.PerProperty,
    isPropertyGroupMarket = !!partner?.features.includes(
      FEATURES.PROPERTY_GROUP_MARKET
    ),
    isOnboardingChecklist = !!partner?.features.includes(
      FEATURES.ONBOARDING_CHECKLIST
    ),
    isAddressValidationEnabled = !!partner?.features.includes(
      FEATURES.ADDRESS_VALIDATION
    ),
    isOwner = auth?.role === ROLE.Owner,
    isAdmin = auth?.role === ROLE.Admin,
    adminPrivileges = isOwner || isAdmin,
    isPropertyManager = !adminPrivileges,
    allowPropertyManagementFeature = partner?.features.includes(
      FEATURES.ALLOW_MANUAL_PROPERTY_MANAGEMENT
    ),
    isPmsListingSyncEnabled = partner?.features.includes(
      FEATURES.PMS_LISTING_SYNC
    ),
    canManageEntitiesDirectly =
      isPMS &&
      isRentReporting &&
      isAnyPmsActive({ partner, pmsList: DirectBoomCreationPMSes, isMultiPMS });

  const userTimeZone = "America/New_York" as string | undefined; // Replace with actual user time zone logic

  return (
    <AppContext.Provider
      value={{
        auth,
        userTimeZone:
          userTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        partner,
        refreshAuth,
        dashboard,
        partnerDefaultEnrollments,
        refreshPartnerData,
        refreshPartnerDefaultEnrollments,
        refreshAuthUserDashboardState,
        permissions: {
          canChangeAvailablebyDate:
            adminPrivileges || Boolean(auth?.can_change_available_by_date),
          canChangeRentAmount:
            adminPrivileges || Boolean(auth?.can_change_rent_amount),
          canMakeDecisions:
            adminPrivileges || Boolean(auth?.can_make_decisions),
          canViewReports: adminPrivileges || Boolean(auth?.can_view_reports),
          canUpdateBillingType:
            adminPrivileges || Boolean(partner?.can_update_billing_type),
          canManageProperties:
            (adminPrivileges && allowPropertyManagementFeature) ||
            canManageEntitiesDirectly,
          canCancelApplications:
            adminPrivileges || Boolean(auth?.can_cancel_applications),
          canChangeUnit: adminPrivileges || Boolean(auth?.can_change_unit),
          canOverrideDecisionRecommendation:
            adminPrivileges ||
            Boolean(auth?.can_override_decision_recommendation),
          canEditIncome: adminPrivileges || Boolean(auth?.can_edit_income),
          canManageAllProperties:
            adminPrivileges || Boolean(auth?.can_manage_all_properties),
          canShowFullSsn: adminPrivileges || Boolean(auth?.can_show_full_ssn),
          canChangeApplicantRole:
            adminPrivileges || Boolean(auth?.can_change_applicant_role),
          canSubscribeRenters:
            adminPrivileges || Boolean(auth?.can_subscribe_renters),
          canEditSubscriptionDetails:
            adminPrivileges || Boolean(auth?.can_edit_subscription_details),
          canUnsubscribeRenters:
            adminPrivileges || Boolean(auth?.can_unsubscribe_renters),
          canViewRentersList:
            adminPrivileges || Boolean(auth?.can_view_renters_list),
          canViewRentReporting:
            adminPrivileges || Boolean(auth?.can_view_rent_reporting),
          canViewCreditReporting:
            adminPrivileges || Boolean(auth?.can_view_credit_reporting),
          boomReportWriteAccess:
            adminPrivileges ||
            Boolean(
              auth?.can_subscribe_renters || auth?.can_edit_subscription_details
            ),
          canManageDiscounts:
            adminPrivileges || Boolean(auth?.can_access_discount_codes),
          canApplyActiveDiscounts:
            adminPrivileges || Boolean(auth?.can_apply_active_discounts),
          canApplyCustomDiscounts:
            adminPrivileges || Boolean(auth?.can_apply_custom_discounts),
          canRunReportsManually:
            adminPrivileges || Boolean(auth?.can_run_reports_manually),
          canEditApplicationRent:
            adminPrivileges || Boolean(auth?.can_edit_application_rent),
          canOverrideApplicantScreeningTemplate:
            adminPrivileges ||
            Boolean(auth?.can_override_applicant_screening_template),
          canAccessScreeningTemplates:
            adminPrivileges || Boolean(auth?.can_access_screening_templates),
          canAccessReports: {
            rentalApplication:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.rental_application),
            identityVerification:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.identity_verification),
            incomeReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.income_report),
            housingHistoryReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.housing_history_report),
            creditReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.credit_report),
            usCriminalReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.us_criminal_report),
            caCriminalReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.ca_criminal_report),
            caCreditReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.ca_credit_report),
            housingCourtReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.housing_court_report),
            animalScreening:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.animal_screening),
            disclosure:
              adminPrivileges || Boolean(auth?.can_access_reports?.disclosure),
            additionalDocumentsReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.additional_documents),
            warningsReport:
              adminPrivileges ||
              Boolean(auth?.can_access_reports?.warnings_report)
          },
          canEditIdv: {
            overrideFailedIdv:
              adminPrivileges ||
              Boolean(auth?.can_edit_idv?.override_failed_idv),
            retryFailedIdv:
              adminPrivileges || Boolean(auth?.can_edit_idv?.retry_failed_idv),
            rerunIdvReport:
              adminPrivileges || Boolean(auth?.can_edit_idv?.rerun_idv_report),
            editIdvDetails:
              adminPrivileges || Boolean(auth?.can_edit_idv?.edit_idv_details)
          },
          canResendInvitation:
            adminPrivileges || Boolean(auth?.can_resend_invitation),
          canCopyApplicationLink:
            adminPrivileges || Boolean(auth?.can_copy_application_link),
          canAccessBillingSettings:
            adminPrivileges || Boolean(auth?.can_access_billing_settings),
          canAccessUsersSettings:
            adminPrivileges || Boolean(auth?.can_access_users_settings),
          canChangeDesiredMoveInDate:
            adminPrivileges || Boolean(auth?.can_change_desired_move_in_date),
          canMakeVoiceCalls:
            adminPrivileges || Boolean(auth?.can_make_voice_calls),
          // No `adminPrivileges ||` here on purpose (CRM-1169): inbound eligibility
          // is a column the API returns verbatim, and the whole point of the toggle
          // is that an admin can be opted out of ringing.
          receivesInboundCalls: Boolean(auth?.receives_inbound_calls),
          canRefundApplication:
            adminPrivileges || Boolean(auth?.can_refund_application),
          canAccessExplainRecommendationModal:
            adminPrivileges ||
            Boolean(auth?.can_access_explain_recommendation_modal),
          canEditEstimatedMonthlyPayments:
            adminPrivileges ||
            Boolean(auth?.can_edit_estimated_monthly_payments)
        },
        partnerFeatures: partner?.partner_features || [],
        type: {
          isManual,
          isSDK,
          isPMS,
          isBoomScreen,
          isBoomCRM,
          isProvisionedPhoneNumber,
          isCommunication,
          isCommunicationSms,
          isNativeChat,
          isNativeChatActive,
          isCommunicationPhone,
          isCrmAiVoiceAgent,
          isCrmFeatureWorkflows,
          isTenantLeads,
          isTasks,
          isLockboxes,
          isCollectionsOutstandingBalance,
          isMasterSearch,
          isSyndication,
          isShowings,
          isMarketRent,
          isCrmListingSync,
          isPmsListingSync,
          isListingSettings,
          isCommunicationEmail,
          isCrmAnalytics,
          isCustomizeLeadSourcesStages,
          isOwnerLeads,
          isRentReporting,
          isPropertyManager,
          adminPrivileges,
          isBillPerProperty,
          isPropertyGroupMarket,
          isOnboardingChecklist,
          isAddressValidationEnabled,

          isFullService: partner?.service_type === SERVICE_TYPE.FULL_SERVICE,
          isLandlord: partner?.service_type === SERVICE_TYPE.LANDLORD,
          isBoomOps: Boolean(String(auth?.email).includes("@boompay.app")),
          isSitesAndCommunities: Boolean(
            partner?.features.includes(FEATURES.SITES_AND_COMMUNITIES)
          ),
          canManageEntitiesDirectly,
          isShowFullSsn: Boolean(
            partner?.features.includes(FEATURES.SHOW_FULL_SSN)
          ),
          isEnterprisePropertyware: Boolean(
            partner?.features.includes(FEATURES.ENTERPRISE_PROPERTYWARE)
          ),
          isPmsListingSyncEnabled: Boolean(isPmsListingSyncEnabled),
          isHomeSiteRentEnabled: Boolean(
            partner?.features.includes(FEATURES.HOME_SITE_RENT)
          ),
          isWeeklyBillingEnabled: Boolean(
            partner?.features.includes(FEATURES.WEEKLY_BILLING)
          ),
          hasTemplateFeeCollection: Boolean(
            partner?.features.includes(FEATURES.TEMPLATE_FEE_COLLECTION)
          ),
          isMultiPMS,
          isStripeManagedBilling: Boolean(
            partner?.features.includes(FEATURES.STRIPE_MANAGED_BILLING)
          ),
          isBoomScreenOutcomes: Boolean(
            partner?.features.includes(FEATURES.BOOM_SCREEN_OUTCOMES)
          ),
          isCombinedSearchV2: Boolean(
            partner?.features.includes(FEATURES.COMBINED_SEARCH_V2)
          )
        },
        pms: buildPmsFlags({ partner, isMultiPMS })
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export function useAuthContext() {
  return useContext(AppContext);
}

/**
 * Outbound voice calling (CRM-811) is available when the partner has the
 * dedicated `communication_phone` feature (which itself requires a provisioned
 * phone number → communication → BoomCRM) AND the user holds the
 * `can_make_voice_calls` permission. Mirrors the boompay-api gate:
 * `communication_phone` feature on the calls/voice endpoints +
 * `can_make_voice_calls` permission + an active voice-capable provisioned number.
 *
 * This gates the outbound affordances only (dial buttons, the inbox voice tab).
 * Use `useSoftphoneEnabled` to decide whether to register a Twilio Device.
 */
export function useVoiceEnabled() {
  const { type, permissions } = useAuthContext();
  return (
    type.isCommunicationPhone &&
    type.isProvisionedPhoneNumber &&
    permissions.canMakeVoiceCalls
  );
}

/**
 * Whether to mount the softphone and register a Twilio Device. Receiving a call
 * needs a registered Device just as much as placing one does, so either direction
 * qualifies (CRM-1169) — mirroring `BoomCRM::Voice::IssueCapabilityToken`, which
 * mints a token for either and scopes the grant accordingly.
 *
 * Gating this on outbound alone would leave an inbound-only user in the API's ring
 * list with no Device, so the caller would hear 30s of ringing and then a drop.
 */
export function useSoftphoneEnabled() {
  const { type, permissions } = useAuthContext();
  // Collections partners qualify on the balances page alone (BF-552). The CRM
  // entitlements are how calling is sold; the balances page is granted by a UI
  // flag outside any plan, and its Call action needs a registered Device just
  // as much — without this the softphone never mounts and the button sits
  // permanently on "the softphone is not connected".
  const partnerEntitled =
    (type.isCommunicationPhone && type.isProvisionedPhoneNumber) ||
    type.isCollectionsOutstandingBalance;
  return (
    partnerEntitled &&
    (permissions.canMakeVoiceCalls || permissions.receivesInboundCalls)
  );
}
